import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Typography, Paper, Stack} from 'views/components';
import {Translate} from 'views/module';
import {HelpOutline} from 'views/icons';
import {outlinedButtonSx, primaryButtonSx} from '../utils/button_styles';

import RadioInput from './inputs/RadioInput';
import CheckboxInput from './inputs/CheckboxInput';
import TextInput from './inputs/TextInput';
import PhoneNumberInput from './inputs/PhoneNumberInput';
import EmailInput from './inputs/EmailInput';
import AutocompleteInput from './inputs/AutocompleteInput';

const FormQuestion = (props) => {
  const {
    question,
    buttonLabel,
    onNext,
    onPrevious,
    answer,
    isLoading,
    isSubmit,
    showPopper,
  } = props;

  let showNextButton = true;

  const [radioValue, setRadioValue] = React.useState('');
  const [checkboxValues, setCheckboxValues] = React.useState([]);
  const [otherText, setOtherText] = React.useState('');
  const [textValue, setTextValue] = React.useState('');
  const [dropdownValue, setDropdownValue] = React.useState('');

  React.useEffect(() => {
    setTextValue('');
    setDropdownValue('');
    setRadioValue('');
    setCheckboxValues([]);
    setOtherText('');

    const rawValue = answer?.questionData?.value;
    if (question.fieldType === 'text') {
      setTextValue(rawValue ?? '');
      return;
    }
    if (question.fieldType === 'phoneNumber') {
      setTextValue(rawValue ?? '');
      return;
    }
    if (question.fieldType === 'email') {
      setTextValue(rawValue ?? '');
      return;
    }
    if (question.fieldType === 'dropdown') {
      setDropdownValue(rawValue ?? '');
      return;
    }
    if (question.fieldType === 'radio') {
      const optionValues = (question.options || []).map((option) => option.value);
      if (optionValues.includes(rawValue)) {
        setRadioValue(rawValue);
        return;
      }
      if (rawValue === 'other') {
        setRadioValue('other');
        setOtherText('');
        return;
      }
      if (rawValue) {
        setRadioValue('other');
        setOtherText(String(rawValue));
      }
      return;
    }
    if (question.fieldType === 'checkbox') {
      if (rawValue && typeof rawValue === 'object') {
        setCheckboxValues(rawValue.selected || []);
        setOtherText(rawValue.otherText ?? '');
      } else {
        setCheckboxValues(rawValue || []);
      }
    }
  }, [answer, question.questionId, question.fieldType, question.options]);

  const buildAnswer = (override) => {
    const isObjectQuestion = ['careSettings', 'accreditations', 'careTasks'].includes(question.questionId);
    let payloadFieldType = 'short_text';
    if (isObjectQuestion) {
      payloadFieldType = 'object';
    } else if (question.fieldType === 'checkbox') {
      payloadFieldType = 'checkbox';
    }
    if (question.fieldType === 'radio') {
      const selected = override || radioValue;
      const option = (question.options || []).find((opt) => opt.value === selected);
      const value = option?.isOther ? (otherText || 'other') : selected;
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value,
        },
      };
    }
    if (question.fieldType === 'checkbox') {
      const value = isObjectQuestion ? {selected: checkboxValues, otherText} : checkboxValues;
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value,
        },
      };
    }
    if (question.fieldType === 'text') {
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value: textValue,
        },
      };
    }
    if (question.fieldType === 'phoneNumber') {
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value: textValue,
        },
      };
    }
    if (question.fieldType === 'email') {
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value: textValue,
        },
      };
    }
    if (question.fieldType === 'dropdown') {
      return {
        bioQuestionId: question.questionId,
        fieldType: payloadFieldType,
        questionData: {
          value: dropdownValue,
        },
      };
    }
    return null;
  };

  showNextButton = !(question.fieldType === 'radio' && !radioValue) || isSubmit;

  const handleRadioSelect = async (option) => {
    const nextValue = option.value;
    setRadioValue(nextValue);
    if (!option.isOther) {
      setOtherText('');
      await onNext(buildAnswer(nextValue));
    }
  };

  const handleCheckboxToggle = (optionValue, checked) => {
    const next = checked ?
      [...checkboxValues, optionValue] :
      checkboxValues.filter((v) => v !== optionValue);
    setCheckboxValues(next);
    if (!next.includes('other')) {
      setOtherText('');
    }
  };

  const handleOtherChange = (value) => {
    const nextValue = value ?? '';
    setOtherText(nextValue);
  };

  const handleTextChange = (value) => {
    const nextValue = value ?? '';
    setTextValue(nextValue);
  };

  const handleDropdownChange = (value) => {
    const nextValue = value ?? '';
    setDropdownValue(nextValue);
  };

  const nextDisabled = () => {
    if (isLoading) {
      return true;
    }
    const required = question.questionData?.required;
    if (!required) {
      return false;
    }
    switch (question.fieldType) {
      case 'radio': {
        if (radioValue === 'other') {
          return !Boolean(otherText);
        }
        return !Boolean(radioValue);
      }
      case 'checkbox': {
        if (checkboxValues.includes('other')) {
          return !Boolean(otherText);
        }
        return checkboxValues.length === 0;
      }
      case 'text':
      case 'phoneNumber':
      case 'email':
        return !Boolean(textValue && textValue.trim());
      case 'dropdown':
        return !Boolean(dropdownValue && dropdownValue.trim());
      default:
        return false;
    }
  };

  return (
    <Paper
      sx={{
        padding: {xs: '16px', sm: '24px'},
        borderRadius: '20px',
        boxShadow: 'none',
      }}
    >
      <Stack spacing={{xs: 2, sm: 3}}>
        <Typography
          sx={{
            fontWeight: 250,
            fontSize: {xs: '24px', sm: '28px', md: '32px'},
            color: '#2D2925',
            fontFamily: 'Spectral Light',
            lineHeight: '120%',
            letterSpacing: '-2%',
          }}
        >
          {question?.questionData?.label}
        </Typography>
        <Box>
          {
            question.fieldType === 'radio' &&
            <RadioInput
              questionData={question.questionData}
              options={question.options}
              value={radioValue}
              otherText={otherText}
              onSelect={handleRadioSelect}
              onOtherTextChange={handleOtherChange}
            />
          }
          {
            question.fieldType === 'checkbox' &&
            <CheckboxInput
              questionData={question.questionData}
              options={question.options}
              values={checkboxValues}
              otherText={otherText}
              onToggle={handleCheckboxToggle}
              onOtherTextChange={handleOtherChange}
            />
          }
          {
            question.fieldType === 'text' &&
            <TextInput
              questionData={question.questionData}
              value={textValue}
              onChange={handleTextChange}
            />
          }
          {
            question.fieldType === 'phoneNumber' &&
            <PhoneNumberInput
              questionData={question.questionData}
              value={textValue}
              onChange={handleTextChange}
            />
          }
          {
            question.fieldType === 'email' &&
            <EmailInput
              questionData={question.questionData}
              value={textValue}
              onChange={handleTextChange}
            />
          }
          {
            question.fieldType === 'dropdown' &&
            <AutocompleteInput
              questionData={question.questionData}
              value={dropdownValue}
              onChange={handleDropdownChange}
              options={question.options}
            />
          }
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
            gap: {xs: '12px', sm: '0px'},
            flexWrap: 'nowrap',
          }}
        >
          {
            (onPrevious && question?.rank !== 1) &&
              <Button
                variant='outlined'
                onClick={onPrevious}
                sx={{
                  ...outlinedButtonSx,
                  width: '140px',
                  minWidth: '140px',
                }}
              >
                <Translate expression='form.FormStep.Previous' />
              </Button>
          }
          {
            showNextButton &&
            <Button
              variant='contained'
              onClick={() => onNext(buildAnswer())}
              disabled={nextDisabled()}
              sx={{
                ...primaryButtonSx,
                ...(nextDisabled() ? {
                  backgroundColor: '#DED8D7',
                  color: '#2D2925B8',
                } : {}),
                width: '140px',
                minWidth: '140px',
                marginLeft: onPrevious && question?.rank !== 1 ? 0 : 'auto',
              }}
            >
              {buttonLabel}
            </Button>
          }
        </Box>
        {
          showPopper &&
          <Paper
            sx={{
              width: '100%',
              borderRadius: '16px',
              padding: {xs: '16px', sm: '20px'},
              backgroundColor: '#DA795514',
              boxShadow: 'none',
            }}
          >
            <Stack spacing={{xs: 1, sm: 1}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <HelpOutline sx={{color: '#DA7955', fontSize: {xs: '20px', sm: '24px'}}} />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '150%',
                  }}
                >
                  <Translate expression='form.FormStep.WhyAskingTitle' />
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                }}
              >
                <Translate expression='form.FormStep.WhyAskingBody' />
              </Typography>
            </Stack>
          </Paper>
        }
      </Stack>
    </Paper>
  );
};

FormQuestion.propTypes = {
  question: PropTypes.shape({
    questionId: PropTypes.string.isRequired,
    fieldType: PropTypes.oneOf(['radio', 'checkbox', 'text', 'phoneNumber', 'email', 'dropdown']).isRequired,
    rank: PropTypes.number,
    questionData: PropTypes.shape({
      label: PropTypes.string.isRequired,
      helpText: PropTypes.string,
      required: PropTypes.bool.isRequired,
    }).isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
      label: PropTypes.string.isRequired,
      isOther: PropTypes.bool.isRequired,
    })),
  }).isRequired,
  buttonLabel: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  answer: PropTypes.shape({
    bioQuestionId: PropTypes.string,
    fieldType: PropTypes.string,
    questionData: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ]),
    }),
  }),
  isLoading: PropTypes.bool,
  isSubmit: PropTypes.bool,
  showPopper: PropTypes.bool,
};

export default React.memo(FormQuestion);
